import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { UserMenu } from './UserMenu';
import { HelpModal } from './HelpModal';
import FinanzasPage from './FinanzasPage';
import { authHelper } from '../utils/authHelper';
import { pushNotifications } from '../utils/pushNotifications';
import { useAutoUpdate } from '../hooks/useAutoUpdate';
import { Home } from './Home';
import { Calculator, ContractCalculator, LimitCalculatorBase, ContractCalculatorBase, useCalculatorPopout } from './Calculator';
import { PipFrame } from './PipPortal';
import { BitacoraTable } from './BitacoraTable';
import { PlanTable } from './PlanTable';
import { PracticePage } from './PracticePage';
import { EntryForm } from './EntryForm';
import { AiImportModal } from './AiImportModal';
import { AdminDashboard } from './AdminDashboard';
import { SettingsPage } from './SettingsPage';
import { ControlPanel } from './ControlPanel';
import { formatNumber } from '../utils';
import { buildDayGroups, computeExecutedRows } from '../utils/planLogic';
import {
  Calculator as CalcIcon,
  Coins,
  FileText,
  Plus,
  X,
  Edit,
  Home as HomeIcon,
  BookOpen,
  Target,
  Calendar as CalendarIcon,
  Settings,
  Maximize2,
  Wallet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function NavButton({ active, onClick, icon, label }) {
  return (
    <button
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex items-center gap-1.5 px-2 sm:px-3 py-3 sm:py-3.5 text-[10px] sm:text-xs font-medium border-b-2 transition-all whitespace-nowrap shrink-0 ${
        active
          ? 'border-blue-500 text-blue-600'
          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
      }`}
    >
      {icon}
      <span className="hidden lg:inline">{label}</span>
      <span className="lg:hidden">{label.split(' ').pop().slice(0, 4)}</span>
    </button>
  );
}

function Dashboard() {
  const { t, language, setLanguage } = useLanguage();
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Auto-update hook - checks for new versions and updates automatically
  const { updateAvailable, isUpdating, currentVersion } = useAutoUpdate(API_URL);
  
  const [dataLoaded, setDataLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  const [limitPercentage, setLimitPercentage] = useState(10);
  const [planInitialInvestment, setPlanInitialInvestment] = useState(100);
  const [initialCapital, setInitialCapital] = useState(0);
  const [bitacoraEntries, setBitacoraEntries] = useState([]);
  const [estudioEntries, setEstudioEntries] = useState([]);
  const [confirmedExtractions, setConfirmedExtractions] = useState({});
  const [lastConfirmedExtractions, setLastConfirmedExtractions] = useState(null);
  const [practiceData, setPracticeData] = useState({});
  const [lastBitacoraEntries, setLastBitacoraEntries] = useState(null);
  const [lastEstudioEntries, setLastEstudioEntries] = useState(null);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [showAiImport, setShowAiImport] = useState(false);
  const [aiQueue, setAiQueue] = useState([]);
  const [aiTotal, setAiTotal] = useState(0);
  const [aiTargetBalance, setAiTargetBalance] = useState(null);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasPendingSave, setHasPendingSave] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [extractionNotice, setExtractionNotice] = useState(null);
  const [miniCalc, setMiniCalc] = useState(null); // null | 'calc' | 'contracts'
  const [extraDeposits, setExtraDeposits] = useState([]);
  const [financeData, setFinanceData] = useState({});
  const [accountAdjustment, setAccountAdjustment] = useState(0);
  const pendingSaveRef = useRef(false);
  const latestDataRef = useRef(null);
  // PiP windows live at Dashboard level: they survive tab switches and modals,
  // and close only when the user closes them or the app unmounts
  const calcPip = useCalculatorPopout('calc', 580);
  const contractsPip = useCalculatorPopout('contracts', 480);

  // Get user from location state if available (from OAuth callback)
  const currentUser = location.state?.user || user;

  // Load data when user changes
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    setDataLoaded(false);
    const loadData = () => {
      fetch(`${API_URL}/api/data`, { 
        headers: authHelper.getAuthHeaders()
      })
        .then(res => {
          if (!res.ok) throw new Error('load failed');
          return res.json();
        })
        .then(res => {
          if (cancelled) return;
          if (res.data) {
            setLimitPercentage(res.data.limitPercentage ?? 10);
            setPlanInitialInvestment(res.data.planInitialInvestment ?? 100);
            setInitialCapital(res.data.initialCapital ?? 0);
            setBitacoraEntries(res.data.bitacoraEntries ?? []);
            setEstudioEntries(res.data.estudioEntries ?? []);
            setConfirmedExtractions(res.data.confirmedExtractions ?? {});
            setPracticeData(res.data.practiceData ?? {});
            setExtraDeposits(res.data.extraDeposits ?? []);
            setFinanceData(res.data.financeData ?? {});
            setAccountAdjustment(res.data.accountAdjustment ?? 0);
            setLastBitacoraEntries(res.data.lastBitacoraEntries ?? null);
          }
          setDataLoaded(true);
        })
        .catch(() => {
          // NEVER mark data as loaded on failure: saving an empty state would
          // overwrite the user's real data. Retry until the server responds.
          if (!cancelled) setTimeout(loadData, 5000);
        });
    };
    loadData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Auto-sync: Reload data every 30 seconds to sync between devices
  useEffect(() => {
    if (!currentUser) return;

    const syncInterval = setInterval(() => {
      // Don't sync if there's a pending save
      if (hasPendingSave || isSaving) {
        console.log('[Auto-sync] Skipped - save in progress');
        return;
      }

      fetch(`${API_URL}/api/data`, { 
        headers: authHelper.getAuthHeaders()
      })
        .then(res => res.json())
        .then(res => {
          // Skip applying stale server data if a local change is pending save
          if (pendingSaveRef.current) {
            console.log('[Auto-sync] Response discarded - local changes pending');
            return;
          }
          if (res.data) {
            // Only update if data changed (avoid overwriting local changes)
            setLimitPercentage(prev => res.data.limitPercentage ?? prev);
            setPlanInitialInvestment(prev => res.data.planInitialInvestment ?? prev);
            setInitialCapital(prev => res.data.initialCapital ?? prev);
            
            // For bitacora and estudio: only update if the data has changed
            // Don't overwrite if user just made changes
            setBitacoraEntries(prev => {
              const newData = res.data.bitacoraEntries ?? [];
              // Only update if the data from backend is different
              if (JSON.stringify(prev) !== JSON.stringify(newData)) {
                return newData;
              }
              return prev;
            });
            
            setEstudioEntries(prev => {
              const newData = res.data.estudioEntries ?? [];
              if (JSON.stringify(prev) !== JSON.stringify(newData)) {
                return newData;
              }
              return prev;
            });
            
            setConfirmedExtractions(prev => {
              const newData = res.data.confirmedExtractions ?? {};
              if (JSON.stringify(prev) !== JSON.stringify(newData)) {
                return newData;
              }
              return prev;
            });
            
            setPracticeData(res.data.practiceData ?? {});
            setExtraDeposits(prev => {
              const newData = res.data.extraDeposits ?? [];
              return JSON.stringify(prev) !== JSON.stringify(newData) ? newData : prev;
            });
            setFinanceData(prev => {
              const newData = res.data.financeData ?? {};
              return JSON.stringify(prev) !== JSON.stringify(newData) ? newData : prev;
            });
            setAccountAdjustment(prev => res.data.accountAdjustment ?? prev);
            setLastBitacoraEntries(res.data.lastBitacoraEntries ?? null);
          }
        })
        .catch(err => console.error('Auto-sync failed:', err));
    }, 30000); // Every 30 seconds

    return () => clearInterval(syncInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, hasPendingSave, isSaving]);

  // Save data when state changes (debounced)
  useEffect(() => {
    if (currentUser && dataLoaded) {
      setIsSaving(true);
      setHasPendingSave(true);
      const data = {
        limitPercentage,
        planInitialInvestment,
        initialCapital,
        bitacoraEntries,
        estudioEntries,
        confirmedExtractions,
        practiceData,
        extraDeposits,
        financeData,
        accountAdjustment,
        lastBitacoraEntries
      };
      pendingSaveRef.current = true;
      latestDataRef.current = data;
      const timeoutId = setTimeout(() => {
        fetch(`${API_URL}/api/data`, {
          method: 'POST',
          headers: authHelper.getAuthHeaders(),
          body: JSON.stringify({ data })
        }).finally(() => {
          pendingSaveRef.current = false;
          setIsSaving(false);
          setHasPendingSave(false);
        });
      }, 2000);
      return () => clearTimeout(timeoutId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, limitPercentage, planInitialInvestment, initialCapital, bitacoraEntries, estudioEntries, confirmedExtractions, practiceData, extraDeposits, financeData, accountAdjustment, lastBitacoraEntries, dataLoaded]);

  // Flush pending save immediately if the user closes/hides the page before the debounce fires
  useEffect(() => {
    const flushPendingSave = () => {
      if (!pendingSaveRef.current || !latestDataRef.current) return;
      fetch(`${API_URL}/api/data`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders(),
        body: JSON.stringify({ data: latestDataRef.current }),
        keepalive: true
      }).catch(() => {});
      pendingSaveRef.current = false;
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushPendingSave();
    };
    window.addEventListener('beforeunload', flushPendingSave);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', flushPendingSave);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const handleAddDeposit = (amount, dateStr) => {
    const deposit = {
      id: `dep_${Date.now()}`,
      amount,
      date: dateStr 
        ? new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US')
        : new Date().toLocaleDateString('en-US'),
      afterTrade: bitacoraEntries.length
    };
    setExtraDeposits(prev => [...prev, deposit]);
  };

  const handleDeleteDeposit = (depositId) => {
    setExtraDeposits(prev => prev.filter(d => d.id !== depositId));
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Calculate current investment for the next trade (matches PlanTable's availableBalance)
  const calculateCurrentInvestment = () => {
    let totalProfit = 0;
    
    bitacoraEntries.forEach((entry) => {
      totalProfit += entry.profit || 0;
    });
    
    estudioEntries.forEach((entry) => {
      totalProfit += entry.profit || 0;
    });
    
    const totalExtracted = Object.values(confirmedExtractions).reduce((sum, ex) => sum + (ex.amount || 0), 0);
    const totalDeposits = extraDeposits.reduce((sum, d) => sum + (Number(d.amount) || 0), 0);
    
    // Total account = capital + deposits + real profits/losses - extractions + manual adjustment (never negative)
    const totalAccount = Math.max(0, initialCapital + totalDeposits + totalProfit - totalExtracted + accountAdjustment);
    return totalAccount / 3;
  };

  const handleConfirmExtraction = (tradeIndex, amount) => {
    setConfirmedExtractions(prev => ({
      ...prev,
      [tradeIndex]: { amount, date: new Date().toISOString() }
    }));
  };

  const handleUndoExtraction = (tradeIndex) => {
    setConfirmedExtractions(prev => {
      const updated = { ...prev };
      delete updated[tradeIndex];
      return updated;
    });
  };

  const notifyExtractionAvailable = (amount) => {
    setExtractionNotice(amount);
    
    const title = 'MÉTODO 11';
    const body = language === 'es' 
      ? `🎉 ¡Tienes una extracción disponible de $${formatNumber(amount)}! Ve al Plan para confirmarla.` 
      : `🎉 You have an extraction available of $${formatNumber(amount)}! Go to the Plan to confirm it.`;
    
    const triggerSystemPush = () => {
      if (pushNotifications.isSupported()) {
        // System-level push via Service Worker (works even with app closed)
        pushNotifications.subscribe()
          .then(() => pushNotifications.notifyExtraction(amount, language))
          .catch(() => {
            try { new Notification(title, { body, icon: '/logo192.png', badge: '/logo192.png' }); } catch (e) { console.debug('Notification fallback failed:', e); }
          });
      } else {
        try { new Notification(title, { body, icon: '/logo192.png', badge: '/logo192.png' }); } catch (e) { console.debug('Notification not available:', e); }
      }
    };

    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        triggerSystemPush();
      } else if (Notification.permission === 'default') {
        Notification.requestPermission().then(p => {
          if (p === 'granted') triggerSystemPush();
        });
      }
    }
  };

  const handleSaveEntry = (entry) => {
    let bitList = bitacoraEntries;
    let estList = estudioEntries;
    if (entry.profit < 0) {
      estList = [entry, ...estudioEntries];
      setEstudioEntries(estList);
      setActiveTab('estudio');
    } else {
      bitList = [entry, ...bitacoraEntries];
      setBitacoraEntries(bitList);
      setActiveTab('bitacora');
      
      // Notify only if this entry completes a 5-day positive streak with a positive cycle
      const rows = computeExecutedRows({
        dayGroups: buildDayGroups(bitList, estudioEntries),
        percentage: limitPercentage,
        initialCapital,
        confirmedExtractions,
        startingBalance: initialCapital + extraDeposits.reduce((s, d) => s + (Number(d.amount) || 0), 0) + accountAdjustment
      });
      const lastRow = rows[rows.length - 1];
      if (lastRow && lastRow.extraction > 0 && !confirmedExtractions[lastRow.index]) {
        notifyExtractionAvailable(lastRow.extraction);
      }
    }
    // If more AI-read entries are queued, open the next one for review
    if (advanceAiQueue()) return;
    applyAiBalanceSync(bitList, estList);
    setEditingEntry(null);
    setShowEntryForm(false);
  };

  const handleUpdateEntry = (updatedEntry) => {
    const wasInBitacora = bitacoraEntries.some(e => e.id === updatedEntry.id);
    const wasInEstudio = estudioEntries.some(e => e.id === updatedEntry.id);
    let bitList = bitacoraEntries;
    let estList = estudioEntries;

    if (updatedEntry.profit < 0) {
      if (wasInEstudio) {
        estList = estudioEntries.map(e => e.id === updatedEntry.id ? updatedEntry : e);
      } else {
        bitList = bitacoraEntries.filter(e => e.id !== updatedEntry.id);
        estList = [updatedEntry, ...estudioEntries];
      }
      setBitacoraEntries(bitList);
      setEstudioEntries(estList);
      setActiveTab('estudio');
    } else {
      if (wasInBitacora) {
        bitList = bitacoraEntries.map(e => e.id === updatedEntry.id ? updatedEntry : e);
      } else {
        estList = estudioEntries.filter(e => e.id !== updatedEntry.id);
        bitList = [updatedEntry, ...bitacoraEntries];
      }
      setBitacoraEntries(bitList);
      setEstudioEntries(estList);
      setActiveTab('bitacora');
    }
    // If more AI-read entries are queued, open the next one for review
    if (advanceAiQueue()) return;
    applyAiBalanceSync(bitList, estList);
    setEditingEntry(null);
    setShowEntryForm(false);
  };

  const deleteBitacoraEntry = (id) => {
    if (window.confirm(t.bitacora.deleteConfirm)) {
      setBitacoraEntries(bitacoraEntries.filter(e => e.id !== id));
    }
  };

  const deleteEstudioEntry = (id) => {
    if (window.confirm(t.bitacora.deleteConfirm)) {
      setEstudioEntries(estudioEntries.filter(e => e.id !== id));
    }
  };

  const startEditEntry = (entry) => {
    setEditingEntry(entry);
    setShowEntryForm(true);
  };

  const mapAiEntry = (data, idx) => {
    const purchases = (data.purchases || [])
      .map(p => ({ time: p.time || '', qty: Number(p.qty) || 0, buyPrice: Number(p.buyPrice) || 0 }))
      .filter(p => p.qty > 0 && p.buyPrice > 0);
    const draft = {
      __aiDraft: true,
      __draftKey: `ai-${Date.now()}-${idx}`,
      date: data.date || '',
      symbol: data.symbol || '',
      strike: Number(data.strike) || 0,
      type: /put/i.test(String(data.type)) ? 'Put' : 'Call',
      purchases,
      sellDate: data.sellDate || data.date || '',
      sellTime: data.sellTime || '',
      sellPrice: Number(data.sellPrice) || 0,
      brokerFee: Number(data.brokerFee) || 0,
      miscFee: Number(data.miscFee) || 0
    };
    // If an entry for the same day + contract already exists, the AI UPDATES it instead of duplicating
    const existing = [...bitacoraEntries, ...estudioEntries].find(e =>
      e.date === draft.date &&
      String(e.symbol || '').toUpperCase() === String(draft.symbol).toUpperCase() &&
      Number(e.strike || 0) === draft.strike &&
      (e.type || 'Call') === draft.type
    );
    if (existing) {
      draft.id = existing.id;
      draft.__aiUpdate = true;
      if (existing.notes) draft.notes = existing.notes;
    }
    return draft;
  };

  const handleAiParsed = (data) => {
    const rawEntries = Array.isArray(data?.entries) ? data.entries : [data];
    const drafts = rawEntries.map(mapAiEntry);
    if (drafts.length === 0) return;
    const lastEnd = Number(rawEntries[rawEntries.length - 1]?.dayEndBalance);
    setAiTargetBalance(Number.isFinite(lastEnd) && lastEnd > 0 ? lastEnd : null);
    setAiQueue(drafts.slice(1));
    setAiTotal(drafts.length);
    setEditingEntry(drafts[0]);
    setShowAiImport(false);
    setShowEntryForm(true);
  };

  // After the last AI entry is saved, adjust Total Cuenta so it matches the broker's end-of-day balance
  const applyAiBalanceSync = (bitList, estList) => {
    if (aiTargetBalance == null) return;
    const totalDeposits = extraDeposits.reduce((s, d) => s + (Number(d.amount) || 0), 0);
    const rows = computeExecutedRows({
      dayGroups: buildDayGroups(bitList, estList),
      percentage: limitPercentage,
      initialCapital,
      confirmedExtractions,
      startingBalance: initialCapital + totalDeposits + accountAdjustment
    });
    const base = initialCapital + totalDeposits + rows.reduce((s, r) => s + r.accountDelta, 0);
    const adj = Number((aiTargetBalance - base).toFixed(2));
    setAiTargetBalance(null);
    // The screenshot's end-of-day balance IS the real account value: apply silently
    setAccountAdjustment(Math.abs(adj) < 0.01 ? 0 : adj);
  };

  const advanceAiQueue = () => {
    if (aiQueue.length > 0) {
      const [next, ...rest] = aiQueue;
      setAiQueue(rest);
      setEditingEntry(next);
      setShowEntryForm(true);
      return true;
    }
    setAiTotal(0);
    return false;
  };

  const resetPlan = () => {
    if (bitacoraEntries.length > 0) {
      if (window.confirm(t.plan.resetConfirm)) {
        setLastBitacoraEntries([...bitacoraEntries]);
        setLastConfirmedExtractions({ ...confirmedExtractions });
        setBitacoraEntries([]);
        setConfirmedExtractions({});
      }
    }
  };

  const mergeEntries = (current, snapshot) => {
    const ids = new Set(current.map(e => e.id));
    return [...current, ...snapshot.filter(e => !ids.has(e.id))];
  };

  const undoReset = () => {
    if (lastBitacoraEntries) {
      const restoredCount = lastBitacoraEntries.filter(e => !bitacoraEntries.some(c => c.id === e.id)).length;
      const msg = language === 'es'
        ? `¿Restaurar las ${restoredCount} entradas del último reinicio? Las entradas actuales se conservarán.`
        : `Restore the ${restoredCount} entries from the last reset? Current entries will be kept.`;
      if (!window.confirm(msg)) return;
      setBitacoraEntries(mergeEntries(bitacoraEntries, lastBitacoraEntries));
      setLastBitacoraEntries(null);
      if (lastConfirmedExtractions) {
        setConfirmedExtractions({ ...lastConfirmedExtractions, ...confirmedExtractions });
        setLastConfirmedExtractions(null);
      }
    }
  };

  // Reset/Undo for Bitacora
  const resetBitacora = () => {
    setLastBitacoraEntries([...bitacoraEntries]);
    setBitacoraEntries([]);
  };

  const undoBitacoraReset = () => {
    if (lastBitacoraEntries) {
      const restoredCount = lastBitacoraEntries.filter(e => !bitacoraEntries.some(c => c.id === e.id)).length;
      const msg = language === 'es'
        ? `¿Restaurar las ${restoredCount} entradas del último reinicio de la bitácora? Las entradas actuales se conservarán.`
        : `Restore the ${restoredCount} entries from the last journal reset? Current entries will be kept.`;
      if (!window.confirm(msg)) return;
      setBitacoraEntries(mergeEntries(bitacoraEntries, lastBitacoraEntries));
      setLastBitacoraEntries(null);
    }
  };

  // Reset/Undo for Estudio
  const resetEstudio = () => {
    setLastEstudioEntries([...estudioEntries]);
    setEstudioEntries([]);
  };

  const undoEstudioReset = () => {
    if (lastEstudioEntries) {
      const restoredCount = lastEstudioEntries.filter(e => !estudioEntries.some(c => c.id === e.id)).length;
      const msg = language === 'es'
        ? `¿Restaurar las ${restoredCount} entradas del último reinicio de estudio? Las entradas actuales se conservarán.`
        : `Restore the ${restoredCount} entries from the last study reset? Current entries will be kept.`;
      if (!window.confirm(msg)) return;
      setEstudioEntries(mergeEntries(estudioEntries, lastEstudioEntries));
      setLastEstudioEntries(null);
    }
  };

  // Pop-out window mode: render only the requested calculator (original behavior)
  const popoutType = new URLSearchParams(window.location.search).get('popout');
  if (popoutType === 'calc' || popoutType === 'contracts') {
    return (
      <div className="app-shell min-h-screen bg-gray-50 p-2" data-testid="popout-view">
        {popoutType === 'calc' 
          ? <Calculator percentage={limitPercentage} onPercentageChange={setLimitPercentage} isMini />
          : <ContractCalculator isMini />}
      </div>
    );
  }

  return (
    <div className="app-shell min-h-screen bg-gray-50" data-testid="dashboard">
      {/* Extraction Available Banner */}
      {extractionNotice !== null && (
        <div className="fixed top-0 left-0 right-0 z-[9998] animate-slide-in-right" data-testid="extraction-notice-banner">
          <div className="bg-gradient-to-r from-indigo-600 to-emerald-600 text-white px-4 py-3 shadow-2xl flex items-center justify-between gap-3 flex-wrap">
            <p className="font-bold text-sm sm:text-base flex-1 min-w-[200px]">
              🎉 {language === 'es' 
                ? `¡Tienes una extracción disponible de $${formatNumber(extractionNotice)}!` 
                : `You have an extraction available of $${formatNumber(extractionNotice)}!`}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setActiveTab('plan'); setExtractionNotice(null); }}
                className="px-4 py-1.5 bg-white text-indigo-700 font-bold rounded-lg text-xs sm:text-sm hover:bg-indigo-50 transition-colors active:scale-95"
                data-testid="extraction-notice-go-plan-btn"
              >
                {language === 'es' ? 'Ir al Plan' : 'Go to Plan'}
              </button>
              <button
                onClick={() => setExtractionNotice(null)}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                data-testid="extraction-notice-close-btn"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Update overlay */}
      {(updateAvailable || isUpdating) && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
            <div className="mb-4">
              <div className="inline-block p-4 bg-blue-100 rounded-full mb-4">
                <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {language === 'es' ? '🔄 Actualizando Aplicación' : '🔄 Updating App'}
            </h2>
            <p className="text-gray-600 mb-4">
              {language === 'es' 
                ? 'Instalando la última versión... Tus datos están seguros. No cierres esta ventana.' 
                : 'Installing latest version... Your data is safe. Don\'t close this window.'}
            </p>
            <div className="text-sm text-gray-500">
              v{currentVersion}
            </div>
          </div>
        </div>
      )}
      
      <nav className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-1 sm:px-3">
          <div className="flex items-center flex-nowrap">
            <div className="flex items-center flex-nowrap flex-1 min-w-0 overflow-x-auto no-scrollbar md:justify-center">
              <NavButton active={activeTab === 'home'} onClick={() => setActiveTab('home')} icon={<HomeIcon size={16} />} label={t.nav.home} />
              <NavButton active={activeTab === 'calc'} onClick={() => setActiveTab('calc')} icon={<CalcIcon size={16} />} label={t.nav.limit} />
              <NavButton active={activeTab === 'contracts'} onClick={() => setActiveTab('contracts')} icon={<Coins size={16} />} label={t.nav.contracts} />
              <NavButton active={activeTab === 'bitacora'} onClick={() => setActiveTab('bitacora')} icon={<FileText size={16} />} label={t.nav.bitacora} />
              <NavButton active={activeTab === 'estudio'} onClick={() => setActiveTab('estudio')} icon={<BookOpen size={16} />} label={t.nav.estudio} />
              <NavButton active={activeTab === 'plan'} onClick={() => setActiveTab('plan')} icon={<Target size={16} />} label={t.nav.plan} />
              <NavButton active={activeTab === 'practica'} onClick={() => setActiveTab('practica')} icon={<CalendarIcon size={16} />} label={t.nav.practice} />
              <NavButton active={activeTab === 'finanzas'} onClick={() => setActiveTab('finanzas')} icon={<Wallet size={16} />} label={language === 'es' ? 'Finanzas' : 'Finances'} />
              {currentUser.isAdmin && (
                <NavButton active={activeTab === 'admin'} onClick={() => setActiveTab('admin')} icon={<Settings size={16} />} label={t.nav.admin} />
              )}
              {currentUser.isAdmin && (
                <NavButton active={activeTab === 'control'} onClick={() => setActiveTab('control')} icon={<Settings size={16} />} label="Panel Control" />
              )}
            </div>

            <div className="flex items-center gap-1.5 sm:gap-2 pl-1.5 shrink-0">
              {/* Language Selector Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                  className="flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white hover:bg-gray-100 transition-all shadow-sm border border-gray-200 overflow-hidden active:scale-95"
                  title="Cambiar idioma"
                  data-testid="language-toggle"
                >
                  <img 
                    src={
                      language === 'es' ? "https://flagcdn.com/w40/es.png" :
                      language === 'en' ? "https://flagcdn.com/w40/us.png" :
                      language === 'fr' ? "https://flagcdn.com/w40/fr.png" :
                      language === 'pt' ? "https://flagcdn.com/w40/br.png" :
                      "https://flagcdn.com/w40/it.png"
                    }
                    alt={language.toUpperCase()}
                    className="w-full h-full object-cover rounded-full"
                    referrerPolicy="no-referrer"
                  />
                </button>

                {showLanguageMenu && (
                  <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                    {[
                      { code: 'es', name: 'Español', flag: 'https://flagcdn.com/w40/es.png' },
                      { code: 'en', name: 'English', flag: 'https://flagcdn.com/w40/us.png' },
                      { code: 'fr', name: 'Français', flag: 'https://flagcdn.com/w40/fr.png' },
                      { code: 'pt', name: 'Português', flag: 'https://flagcdn.com/w40/br.png' },
                      { code: 'it', name: 'Italiano', flag: 'https://flagcdn.com/w40/it.png' },
                    ].map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          setLanguage(lang.code);
                          setShowLanguageMenu(false);
                        }}
                        className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors ${
                          language === lang.code ? 'bg-emerald-50' : ''
                        }`}
                      >
                        <img 
                          src={lang.flag} 
                          alt={lang.name}
                          className="w-6 h-6 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                        <span className="text-sm font-medium text-gray-700">{lang.name}</span>
                        {language === lang.code && (
                          <span className="ml-auto text-emerald-600">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* User Menu (email, settings, help, logout) */}
              <UserMenu
                user={currentUser}
                onSettings={() => setActiveTab('settings')}
                onHelp={() => setShowHelpModal(true)}
                onLogout={handleLogout}
              />
            </div>
          </div>
        </div>
      </nav>

      {showHelpModal && <HelpModal onClose={() => setShowHelpModal(false)} />}

      {/* Dashboard-level PiP portals: persist until user closes them or leaves the app */}
      {calcPip.pipWindow && (
        <PipFrame pipWindow={calcPip.pipWindow} title={t.calc.limitTitle} barClass="bg-emerald-600">
          <LimitCalculatorBase percentage={limitPercentage} onPercentageChange={setLimitPercentage} isMini />
        </PipFrame>
      )}
      {contractsPip.pipWindow && (
        <PipFrame pipWindow={contractsPip.pipWindow} title={t.calc.contractsTitle} barClass="bg-amber-600">
          <ContractCalculatorBase isMini />
        </PipFrame>
      )}

      {/* Floating mini calculator (50% scale, persists across tabs) */}
      {miniCalc && !(activeTab === 'calc' && miniCalc === 'calc') && !(activeTab === 'contracts' && miniCalc === 'contracts') && (
        <div
          className="fixed bottom-2 right-0 z-40"
          style={{ transform: 'scale(0.5)', transformOrigin: 'bottom right' }}
          data-testid="mini-calculator"
        >
          <div className="relative rounded-xl shadow-2xl ring-2 ring-emerald-500/40">
            <button
              onClick={() => setMiniCalc(null)}
              className="absolute -top-3 -left-3 z-10 w-9 h-9 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg font-bold text-lg"
              title={language === 'es' ? 'Cerrar' : 'Close'}
              data-testid="mini-calc-close-btn"
            >
              ✕
            </button>
            <button
              onClick={() => { setActiveTab(miniCalc); setMiniCalc(null); }}
              className="absolute -top-3 left-8 z-10 w-9 h-9 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center shadow-lg"
              title={language === 'es' ? 'Expandir' : 'Expand'}
              data-testid="mini-calc-expand-btn"
            >
              <Maximize2 size={16} />
            </button>
            {miniCalc === 'calc' 
              ? <Calculator percentage={limitPercentage} onPercentageChange={setLimitPercentage} isMini />
              : <ContractCalculator isMini />}
          </div>
        </div>
      )}

      <main className="py-6 sm:py-10 px-2 sm:px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {showEntryForm && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6 relative">
                <button onClick={() => { setShowEntryForm(false); setEditingEntry(null); setAiQueue([]); setAiTotal(0); setAiTargetBalance(null); }} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" data-testid="close-entry-form">
                  <X size={24} />
                </button>
                <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                  {editingEntry && !editingEntry.__aiDraft ? <Edit className="text-blue-600" /> : <Plus className="text-emerald-600" />}
                  {editingEntry && !editingEntry.__aiDraft ? t.form.editEntry : t.form.newEntry}
                </h3>
                {editingEntry?.__aiDraft && (
                  <div className="mb-4 text-xs sm:text-sm font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2" data-testid="ai-draft-banner">
                    {language === 'es'
                      ? `✨ Datos leídos por IA${aiTotal > 1 ? ` — Entrada ${aiTotal - aiQueue.length} de ${aiTotal}` : ''}. ${editingEntry.__aiUpdate ? `Se ACTUALIZARÁ la entrada existente del ${editingEntry.date}.` : ''} Revisa y corrige lo necesario antes de guardar.`
                      : `✨ Data read by AI${aiTotal > 1 ? ` — Entry ${aiTotal - aiQueue.length} of ${aiTotal}` : ''}. ${editingEntry.__aiUpdate ? `The existing entry from ${editingEntry.date} will be UPDATED.` : ''} Review and correct as needed before saving.`}
                  </div>
                )}
                <EntryForm 
                  key={editingEntry?.__draftKey || editingEntry?.id || 'new'}
                  onSave={handleSaveEntry} 
                  onUpdate={handleUpdateEntry}
                  initialData={editingEntry}
                  currentInvestment={calculateCurrentInvestment()}
                  percentage={limitPercentage}
                  showFees={!!currentUser.isAdmin}
                />
              </div>
            </div>
          )}

          {activeTab === 'home' && (
            <Home 
              onNavigate={setActiveTab} 
              onNewEntry={() => setShowEntryForm(true)} 
              user={currentUser}
              bitacoraEntries={bitacoraEntries}
              estudioEntries={estudioEntries}
              percentage={limitPercentage}
              initialCapital={initialCapital}
              extraDeposits={extraDeposits}
              confirmedExtractions={confirmedExtractions}
              accountAdjustment={accountAdjustment}
            />
          )}
          {activeTab === 'calc' && <Calculator percentage={limitPercentage} onPercentageChange={setLimitPercentage} onMinimize={() => setMiniCalc('calc')} onPopout={calcPip.onPopout} />}
          {activeTab === 'contracts' && <ContractCalculator onMinimize={() => setMiniCalc('contracts')} onPopout={contractsPip.onPopout} />}
          {activeTab === 'finanzas' && (
            <FinanzasPage 
              financeData={financeData} 
              onFinanceChange={setFinanceData} 
              confirmedExtractions={confirmedExtractions} 
            />
          )}
          {activeTab === 'bitacora' && (
            <BitacoraTable 
              title={t.bitacora.title} 
              entries={bitacoraEntries} 
              onDelete={deleteBitacoraEntry} 
              onEdit={startEditEntry}
              onReset={resetBitacora}
              onUndo={undoBitacoraReset}
              canUndo={!!lastBitacoraEntries}
              onNewEntry={() => setShowEntryForm(true)}
              onAiImport={() => setShowAiImport(true)}
            />
          )}
          {showAiImport && (
            <AiImportModal onClose={() => setShowAiImport(false)} onParsed={handleAiParsed} />
          )}
          {activeTab === 'estudio' && (
            <BitacoraTable 
              title={t.bitacora.estudioTitle} 
              entries={estudioEntries} 
              onDelete={deleteEstudioEntry} 
              onEdit={startEditEntry} 
              onReset={resetEstudio}
              onUndo={undoEstudioReset}
              canUndo={!!lastEstudioEntries}
              variant="red"
            />
          )}
          {activeTab === 'plan' && (
            <PlanTable 
              bitacoraEntries={bitacoraEntries}
              estudioEntries={estudioEntries}
              percentage={limitPercentage}
              initialCapital={initialCapital}
              onInitialCapitalChange={setInitialCapital}
              confirmedExtractions={confirmedExtractions}
              onConfirmExtraction={handleConfirmExtraction}
              onUndoExtraction={handleUndoExtraction}
              onReset={resetPlan}
              onUndo={undoReset}
              canUndo={!!lastBitacoraEntries}
              extraDeposits={extraDeposits}
              onAddDeposit={handleAddDeposit}
              onDeleteDeposit={handleDeleteDeposit}
              accountAdjustment={accountAdjustment}
              onAccountAdjustmentChange={setAccountAdjustment}
            />
          )}
          {activeTab === 'practica' && <PracticePage data={practiceData} onChange={setPracticeData} />}
          {activeTab === 'admin' && currentUser.isAdmin && <AdminDashboard />}
          {activeTab === 'control' && currentUser.isAdmin && <ControlPanel />}
          {activeTab === 'settings' && <SettingsPage user={currentUser} onUpdateUser={setUser} onLogout={handleLogout} />}
        </div>
      </main>
    </div>
  );
}


export default Dashboard;
